import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicApi } from '../lib/api';

/**
 * Stripe/Link-style hosted checkout page.
 *
 * State machine:
 *   idle              → show payment instructions, user sees amount + receiver + NOTE
 *   checking          → user clicked "I've completed payment", we poll /status every 4s
 *   note_mismatch     → backend found a payment, but the note did not match
 *   ask_sender_name   → user clicked "I wrote the wrong note", show sender-name form
 *   submitting_name   → submitting the name claim
 *   manual_review     → claim submitted but still not matched
 *   verified          → success
 */

const POLL_INTERVAL_MS = 2500;  // Fast poll so we catch the receipt within ~2.5s of it landing
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 min → calm fallback
const MERCHANT_SITE_URL = 'https://gamevault999.world';

// -------------------------------- helpers --------------------------------

const CopyButton = ({ value, testId, small = false }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(String(value ?? ''));
    } catch {
      const ta = document.createElement('textarea');
      ta.value = String(value ?? '');
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 ${small ? 'text-[12px] px-1.5 py-1' : 'text-[13px] px-2 py-1'} font-medium rounded-md shrink-0 transition-colors ${copied ? 'text-emerald-700 bg-emerald-50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
    >
      {copied ? (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Copied
        </>
      ) : (
        <>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2h-2M6 21h8a2 2 0 002-2v-8a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy
        </>
      )}
    </button>
  );
};

// -------------------------------- method meta --------------------------------

const CashAppGlyph = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-white">
    <path d="M12.48 5.56c1.37 0 2.69.54 3.68 1.47a.6.6 0 00.83-.02l1.07-1.1a.6.6 0 00-.02-.86A8.37 8.37 0 0012.54 3c-3.02 0-5.7 1.9-5.7 4.76 0 2.46 1.94 3.82 4.62 4.62 2.1.63 3.06 1.14 3.06 2.14 0 1.08-1.2 1.77-2.8 1.77a5 5 0 01-3.78-1.63.6.6 0 00-.86-.01l-1.08 1.07a.6.6 0 00-.02.84 8 8 0 004.08 2.2l-.26 1.4a.6.6 0 00.59.71h1.38a.6.6 0 00.6-.5l.23-1.32c2.94-.24 5.12-1.92 5.12-4.62 0-2.57-2.1-3.9-4.97-4.76-1.7-.5-2.73-.97-2.73-1.9 0-.95 1.02-1.57 2.46-1.57zM9.9 3.58L10.16 2a.6.6 0 01.59-.5h1.42a.6.6 0 01.59.7l-.27 1.6a.6.6 0 01-.6.5h-1.4a.6.6 0 01-.59-.72z"/>
  </svg>
);
const ChimeGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} className="w-5 h-5 text-white">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 12a8 8 0 0114 5.5M20 12a8 8 0 01-14-5.5" />
  </svg>
);
const WalletGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5 text-white">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M21 12h-4a2 2 0 100 4h4M3 7a2 2 0 012-2h14v4H5a2 2 0 01-2-2z" />
  </svg>
);

const METHOD_META = {
  cashapp:    { label: 'Cash App',   short: 'Cash App',   accent: '#00d632', receiverLabel: 'Cashtag',      nameHint: 'Enter the full name shown on your Cash App account (first and last). Cash App shows full names — both must match',         Glyph: CashAppGlyph },
  chime:      { label: 'Chime',      short: 'Chime',      accent: '#1EC677', receiverLabel: 'ChimeSign',    nameHint: 'Enter your full first and last name as shown in Chime. Chime shows only the first letter of your last name — we already handle that',            Glyph: ChimeGlyph },
  venmo:      { label: 'Venmo',      short: 'Venmo',      accent: '#008CFF', receiverLabel: 'Username',     nameHint: 'Enter the full name shown on your Venmo account',            Glyph: WalletGlyph },
  zelle:      { label: 'Zelle',      short: 'Zelle',      accent: '#6D1ED4', receiverLabel: 'Zelle tag',    nameHint: 'Enter the full name shown on your Zelle account',            Glyph: WalletGlyph },
  apple_cash: { label: 'Apple Cash', short: 'Apple Cash', accent: '#111111', receiverLabel: 'Apple ID',     nameHint: 'Enter the full name on your Apple Cash account', Glyph: WalletGlyph },
};

// -------------------------------- page --------------------------------

const PublicCheckoutPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const amount = searchParams.get('amount');

  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uiState, setUiState] = useState('idle');
  const [detailsOpenMobile, setDetailsOpenMobile] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [claimError, setClaimError] = useState('');
  const [netRetry, setNetRetry] = useState(0);

  const pollRef = useRef(null);
  const timeoutRef = useRef(null);

  const clearTimers = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  };

  const mapStatus = useCallback((res) => {
    if (res?.verification_state === 'expired' || res?.expired === true) return 'expired';
    if (res?.status === 'paid' || res?.status === 'verified') return 'verified';
    const v = res?.verification_state;
    if (v === 'verified_by_note' || v === 'verified_by_sender_name') return 'verified';
    if (v === 'note_mismatch' || res?.has_unmatched_receipts) return 'note_mismatch';
    if (v === 'manual_review_required') return 'manual_review';
    return null;
  }, []);

  const pollOnce = useCallback(async () => {
    try {
      const res = await publicApi.getStatus(token);
      setNetRetry(0);
      const next = mapStatus(res);
      if (next === 'verified')       { clearTimers(); setUiState('verified'); }
      else if (next === 'expired')   { clearTimers(); setUiState('expired'); }
      else if (next === 'note_mismatch')  { clearTimers(); setUiState('note_mismatch'); }
      else if (next === 'manual_review')  { clearTimers(); setUiState('manual_review'); }
    } catch {
      setNetRetry((n) => n + 1);
    }
  }, [token, mapStatus]);

  const startPolling = useCallback(() => {
    clearTimers();
    pollOnce();
    pollRef.current = setInterval(pollOnce, POLL_INTERVAL_MS);
    timeoutRef.current = setTimeout(() => {
      clearTimers();
      setUiState((s) => (s === 'checking' ? 'manual_review' : s));
    }, POLL_TIMEOUT_MS);
  }, [pollOnce]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await publicApi.getCheckout(token, amount);
        if (!cancelled) {
          setCheckout(res.checkout);
          if (res.expired) setUiState('expired');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Checkout page not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; clearTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, amount]);

  useEffect(() => {
    if (!checkout) return;
    // Start polling immediately from load — so the page auto-flips to
    // "Verified" the instant the receipt lands, even if the customer never
    // taps "I've completed payment".
    (async () => {
      try {
        const res = await publicApi.getStatus(token);
        const next = mapStatus(res);
        if (next === 'verified') { setUiState('verified'); return; }
        if (next === 'expired')  { setUiState('expired'); return; }
      } catch {}
      if (uiState === 'idle') startPolling();
    })();
    return () => clearTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout]);

  // Instant re-check when the user returns to the tab / window regains focus
  useEffect(() => {
    const onFocus = () => {
      if (uiState === 'checking' || uiState === 'idle') {
        pollOnce();
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && (uiState === 'checking' || uiState === 'idle')) {
        pollOnce();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uiState, pollOnce]);

  const handleIvePaid = () => { setUiState('checking'); startPolling(); };
  const handleWroteWrongNote = () => { setUiState('ask_sender_name'); setClaimError(''); };
  const goToMerchant = () => { window.location.href = MERCHANT_SITE_URL; };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    setClaimError('');
    setUiState('submitting_name');
    try {
      const res = await publicApi.submitClaim(token, firstName.trim(), lastName.trim());
      if (res.verified || res.status === 'paid' || res.status === 'verified' ||
          res.verification_state === 'verified_by_sender_name' ||
          res.verification_state === 'verified_by_note') {
        setUiState('verified');
      } else {
        setUiState('checking');
        startPolling();
      }
    } catch (err) {
      setClaimError(err.message || 'Failed to submit');
      setUiState('ask_sender_name');
    }
  };

  // ------------------------- loading / error -------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <svg className="animate-spin h-8 w-8 text-slate-400" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" className="opacity-25" />
          <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
        </svg>
      </div>
    );
  }

  if (error && !checkout) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Page not found</h1>
          <p className="text-slate-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // ------------------------- derived -------------------------

  const methodType = checkout?.method_type;
  const meta = METHOD_META[methodType] || { label: methodType || 'Payment', short: 'Payment', accent: '#0a2540', receiverLabel: 'Receiver', nameHint: 'Enter your full name', Glyph: WalletGlyph };
  const Glyph = meta.Glyph;
  const amountStr = checkout?.amount != null
    ? `$${Number(checkout.amount).toFixed(2)}${checkout.currency && checkout.currency !== 'USD' ? ' ' + checkout.currency : ''}`
    : '—';
  const merchantName = checkout?.title || meta.label;
  const orderId = (token || '').slice(0, 12).toUpperCase();
  const receiverValue = checkout?.tag_info || '—';
  const note = checkout?.required_note;

  const statusPill = (() => {
    switch (uiState) {
      case 'verified':                                return { text: 'Verified',     cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' };
      case 'expired':                                 return { text: 'Ended',        cls: 'bg-slate-100 text-slate-600 ring-slate-200',     dot: 'bg-slate-400' };
      case 'note_mismatch':                           return { text: 'Action needed',cls: 'bg-amber-50 text-amber-700 ring-amber-200',      dot: 'bg-amber-500' };
      case 'manual_review':                           return { text: 'In review',    cls: 'bg-slate-100 text-slate-700 ring-slate-200',     dot: 'bg-slate-500' };
      case 'checking':
      case 'submitting_name':
      case 'ask_sender_name':                         return { text: 'Checking',     cls: 'bg-blue-50 text-blue-700 ring-blue-200',         dot: 'bg-blue-500' };
      default:                                        return { text: 'Pending',      cls: 'bg-slate-100 text-slate-600 ring-slate-200',     dot: 'bg-slate-400' };
    }
  })();

  // ------------------------- summary card (reused) -------------------------

  const SummaryCard = ({ className = '' }) => (
    <aside
      className={`bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden ${className}`}
      data-testid="summary-card"
    >
      <header className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-slate-900 tracking-tight">Transaction</h3>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ring-1 ${statusPill.cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusPill.dot}`} />
          {statusPill.text}
        </span>
      </header>
      <dl className="px-5 py-4 text-[13px] space-y-3">
        <div className="flex items-start justify-between gap-3">
          <dt className="text-slate-500">Merchant</dt>
          <dd className="text-slate-900 font-medium text-right truncate max-w-[60%]">{merchantName}</dd>
        </div>
        <div className="flex items-start justify-between gap-3">
          <dt className="text-slate-500">Method</dt>
          <dd className="text-slate-900 font-medium inline-flex items-center gap-1.5">
            <span className="w-4 h-4 rounded-[4px] flex items-center justify-center" style={{ backgroundColor: meta.accent }}>
              <span className="scale-[0.55] origin-center"><Glyph /></span>
            </span>
            {meta.short}
          </dd>
        </div>
        <div className="flex items-start justify-between gap-3">
          <dt className="text-slate-500">Amount</dt>
          <dd className="text-slate-900 font-semibold tabular-nums">{amountStr}</dd>
        </div>
        <div className="flex items-start justify-between gap-3 pt-1 border-t border-slate-100 mt-1">
          <dt className="text-slate-500 pt-2">Order ID</dt>
          <dd className="pt-2">
            <span className="font-mono text-[12px] text-slate-700">{orderId}</span>
          </dd>
        </div>
      </dl>
      <footer className="px-5 py-3 bg-slate-50/70 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m6 4H6a2 2 0 01-2-2v-6a2 2 0 012-2h12a2 2 0 012 2v6a2 2 0 01-2 2zM8 11V7a4 4 0 118 0v4" />
        </svg>
        Secure · PCI-aware · Auto verified
      </footer>
    </aside>
  );

  // ------------------------- render -------------------------

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-[ui-sans-serif,-apple-system,system-ui,sans-serif] flex flex-col" data-testid="public-checkout">
      {/* Header */}
      <header className="bg-[#0a2540] text-white">
        <div className="max-w-[1040px] mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={goToMerchant}
              className="p-1.5 -ml-1.5 rounded-md hover:bg-white/10 transition-colors shrink-0"
              data-testid="back-btn"
              aria-label="Back to merchant"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-md bg-white/15 flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="flex items-baseline gap-2 min-w-0">
                <span className="text-[15px] font-semibold tracking-tight truncate">1TapPay</span>
                <span className="text-[12px] text-white/60 truncate hidden sm:inline">· {merchantName}</span>
              </div>
            </div>
          </div>

          {/* Mobile-only details toggle */}
          <button
            type="button"
            onClick={() => setDetailsOpenMobile((v) => !v)}
            data-testid="details-toggle-mobile"
            className="lg:hidden inline-flex items-center gap-1.5 text-[12px] font-medium text-white/85 bg-white/5 hover:bg-white/10 px-2.5 py-1.5 rounded-md transition-colors"
          >
            <span className="tabular-nums font-semibold">{amountStr}</span>
            <svg className={`w-3.5 h-3.5 transition-transform ${detailsOpenMobile ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        {/* Mobile collapsible summary */}
        {detailsOpenMobile && (
          <div className="lg:hidden border-t border-white/5 bg-[#0a2540]">
            <div className="max-w-[1040px] mx-auto px-4 sm:px-6 py-3">
              <SummaryCard className="text-slate-900" />
            </div>
          </div>
        )}
      </header>

      {/* Body */}
      <main className="flex-1">
        <div className="max-w-[1040px] mx-auto px-4 sm:px-6 py-8 sm:py-12 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 lg:gap-10 items-start">
          {/* Main panel */}
          <section className="min-w-0">
            {/* Merchant + amount hero */}
            <div className="mb-6">
              <p className="text-[13px] text-slate-500 font-medium">{merchantName}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-[40px] leading-none font-semibold tracking-tight text-slate-900 tabular-nums" data-testid="amount-hero">
                  {amountStr}
                </span>
              </div>
              <p className="text-[13px] text-slate-500 mt-2">
                Complete your {meta.label} payment to finish checkout.
              </p>
            </div>

            {/* ====================== STATE: VERIFIED ====================== */}
            {uiState === 'verified' && (
              <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-6 sm:p-8" data-testid="state-verified">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[18px] font-semibold text-slate-900 leading-tight">Payment verified</h2>
                    <p className="text-[13px] text-slate-500">Your payment was received successfully.</p>
                  </div>
                </div>
                <div className="mt-6 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={goToMerchant}
                    data-testid="continue-btn"
                    className="w-full py-3 rounded-lg bg-[#0a2540] hover:bg-[#13335c] text-white font-semibold text-[15px] transition-colors"
                  >
                    Back to gamevault999.world
                  </button>
                </div>
              </div>
            )}

            {/* ====================== STATE: EXPIRED ====================== */}
            {uiState === 'expired' && (
              <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-6 sm:p-8" data-testid="state-expired">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[18px] font-semibold text-slate-900 leading-tight">Session ended</h2>
                    <p className="text-[13px] text-slate-500">This checkout link has expired. Your payment has already been processed.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={goToMerchant}
                  data-testid="expired-back-btn"
                  className="mt-6 w-full py-3 rounded-lg bg-[#0a2540] hover:bg-[#13335c] text-white font-semibold text-[15px] transition-colors"
                >
                  Back to gamevault999.world
                </button>
              </div>
            )}

            {/* ====================== STATE: IDLE ====================== */}
            {uiState === 'idle' && (
              <div className="space-y-5">
                {/* Payment method card */}
                <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden" data-testid="state-idle">
                  <header className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ backgroundColor: meta.accent }}>
                        <Glyph />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Payment method</p>
                        <p className="text-[15px] font-semibold text-slate-900 truncate" data-testid="method-name">{meta.label}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ring-1 ${statusPill.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusPill.dot}`} />
                      {statusPill.text}
                    </span>
                  </header>

                  <div className="px-5 sm:px-6 py-4">
                    {/* Send exactly */}
                    <div className="flex items-center justify-between gap-3 py-2.5">
                      <span className="text-[13px] text-slate-500 font-medium">Send exactly</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[15px] font-semibold text-slate-900 tabular-nums" data-testid="row-amount">{amountStr}</span>
                        <CopyButton value={checkout?.amount != null ? Number(checkout.amount).toFixed(2) : ''} testId="row-amount-copy" small />
                      </div>
                    </div>
                    {/* Recipient */}
                    {checkout?.tag_info && (
                      <div className="flex items-center justify-between gap-3 py-2.5 border-t border-slate-100">
                        <span className="text-[13px] text-slate-500 font-medium">Send to</span>
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="text-[15px] text-slate-900 font-mono truncate" data-testid="row-tag">{receiverValue}</span>
                          <CopyButton value={receiverValue} testId="row-tag-copy" small />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Open App card — only when merchant enabled a redirect URL.
                    Sits between the Payment-method and Payment-note cards. */}
                {checkout?.redirect_url && (
                  <a
                    href={checkout.redirect_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="open-app-btn"
                    className="group block bg-white rounded-2xl ring-1 ring-slate-200 hover:ring-slate-300 hover:shadow-md shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-5 sm:px-6 py-4 transition-all"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shadow-sm shrink-0" style={{ backgroundColor: meta.accent }}>
                          <Glyph />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Pay with app</p>
                          <p className="text-[15px] font-semibold text-slate-900 truncate">Open {meta.label}</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1 text-[13px] text-slate-500 group-hover:text-slate-900 transition-colors shrink-0">
                        Continue
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </span>
                    </div>
                  </a>
                )}

                {/* Payment note — hero verification card */}
                {checkout?.note_required !== false && note && (
                  <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden" data-testid="note-card">
                    <header className="px-5 sm:px-6 py-3.5 border-b border-slate-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-[13px] font-semibold text-slate-900">Payment note</span>
                      </div>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-700 bg-blue-50 ring-1 ring-blue-100 px-2 py-0.5 rounded-full">
                        Required for auto verification
                      </span>
                    </header>
                    <div className="px-5 sm:px-6 py-4">
                      <div className="flex items-center justify-between gap-3 bg-blue-50/40 rounded-xl ring-1 ring-blue-100 px-4 py-3.5">
                        <span
                          className="font-mono font-semibold text-[22px] sm:text-[24px] tracking-[0.12em] text-slate-900 break-all"
                          data-testid="required-note"
                        >
                          {note}
                        </span>
                        <CopyButton value={note} testId="required-note-copy" />
                      </div>
                      <p className="text-[12px] text-slate-500 mt-3 leading-snug">
                        Write this note exactly so we can verify your payment automatically. A wrong note may delay your deposit.
                      </p>
                    </div>
                  </div>
                )}

                {/* CTA block */}
                <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-6">
                  {(checkout?.image_url || checkout?.image_data) && (
                    <div className="mb-4 bg-slate-50 rounded-xl ring-1 ring-slate-100 p-3 flex items-center justify-center">
                      <img src={checkout.image_data || checkout.image_url} alt="Payment" className="max-w-full max-h-40 object-contain rounded-md" />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleIvePaid}
                    data-testid="ive-paid-btn"
                    className="w-full py-3 rounded-lg bg-[#0a2540] hover:bg-[#13335c] text-white font-semibold text-[15px] transition-colors"
                  >
                    I’ve completed payment
                  </button>

                  <div className="mt-4 space-y-1 text-center">
                    <p className="text-[12px] text-slate-500">Secure checkout · Auto verification · Instant credit</p>
                    <p className="text-[11px] text-slate-400">Your payment is checked automatically using your payment note.</p>
                  </div>
                </div>
              </div>
            )}

            {/* ====================== STATE: CHECKING ====================== */}
            {uiState === 'checking' && (
              <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden" data-testid="state-checking">
                <header className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" className="opacity-25" />
                      <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
                    </svg>
                    <div>
                      <h2 className="text-[16px] font-semibold text-slate-900 leading-tight">Checking payment…</h2>
                      <p className="text-[13px] text-slate-500">Looking for a matching payment note.</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ring-1 ${statusPill.cls}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusPill.dot}`} />
                    {statusPill.text}
                  </span>
                </header>
                <div className="px-5 sm:px-6 py-5">
                  {note && (
                    <div className="rounded-xl bg-slate-50 ring-1 ring-slate-100 px-4 py-3 mb-4">
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Note we’re matching</p>
                      <p className="font-mono font-semibold text-[17px] tracking-[0.1em] text-slate-900 break-all" data-testid="checking-note">{note}</p>
                    </div>
                  )}
                  <div className="relative h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mb-4">
                    <div className="absolute inset-y-0 w-1/3 bg-blue-500 rounded-full animate-[slide_1.6s_ease-in-out_infinite]" />
                  </div>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-slate-500">This usually takes less than 1 minute.</span>
                    {netRetry >= 2 && <span className="text-amber-600">Network hiccup — retrying…</span>}
                  </div>
                  <p className="text-[12px] text-slate-400 mt-1">Do not close this page.</p>

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      onClick={goToMerchant}
                      data-testid="continue-merchant-checking"
                      className="w-full py-2.5 rounded-lg bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 font-medium text-[14px] transition-colors"
                    >
                      Back to gamevault999.world
                    </button>
                    <button
                      type="button"
                      onClick={() => { clearTimers(); setUiState('idle'); }}
                      data-testid="cancel-checking-btn"
                      className="w-full py-2.5 rounded-lg text-slate-500 hover:text-slate-800 text-[13px] font-medium transition-colors"
                    >
                      Go back to instructions
                    </button>
                  </div>
                </div>
                <style>{`@keyframes slide { 0%{transform:translateX(-100%)} 50%{transform:translateX(120%)} 100%{transform:translateX(320%)} }`}</style>
              </div>
            )}

            {/* ====================== STATE: NOTE MISMATCH ====================== */}
            {uiState === 'note_mismatch' && (
              <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-6" data-testid="state-note-mismatch">
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-amber-50 ring-1 ring-amber-100 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01" />
                      <circle cx="12" cy="12" r="9" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-[17px] font-semibold text-slate-900 leading-tight">We found a payment, but the note did not match</h2>
                    <p className="text-[13px] text-slate-500 mt-1">
                      If this was your payment, we can still verify it using the full name shown on your {meta.label} account.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleWroteWrongNote}
                  data-testid="wrong-note-btn"
                  className="w-full py-3 rounded-lg bg-[#0a2540] hover:bg-[#13335c] text-white font-semibold text-[15px] transition-colors"
                >
                  I wrote the wrong note
                </button>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2.5">
                  <button
                    type="button"
                    onClick={() => { setUiState('checking'); startPolling(); }}
                    data-testid="keep-waiting-btn"
                    className="w-full py-2.5 rounded-lg bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 font-medium text-[13px] transition-colors"
                  >
                    Keep waiting
                  </button>
                  <button
                    type="button"
                    onClick={goToMerchant}
                    data-testid="continue-merchant-mismatch"
                    className="w-full py-2.5 rounded-lg text-slate-500 hover:text-slate-800 text-[13px] font-medium transition-colors"
                  >
                    Back to gamevault999.world
                  </button>
                </div>
              </div>
            )}

            {/* ====================== STATE: ASK SENDER NAME ====================== */}
            {(uiState === 'ask_sender_name' || uiState === 'submitting_name') && (
              <form
                onSubmit={handleNameSubmit}
                className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-6"
                data-testid="state-sender-name"
              >
                <h2 className="text-[17px] font-semibold text-slate-900 leading-tight">Confirm your identity</h2>
                <p className="text-[13px] text-slate-500 mt-1">{meta.nameHint}.</p>

                {claimError && (
                  <div className="mt-4 rounded-lg bg-red-50 ring-1 ring-red-100 px-3 py-2 text-[13px] text-red-700">{claimError}</div>
                )}

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">First name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      data-testid="claim-first-name"
                      className="w-full px-3.5 py-2.5 rounded-lg bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0a2540] focus:outline-none text-[15px] text-slate-900 placeholder-slate-400 transition-shadow"
                      placeholder="John"
                      required
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-1">Last name</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      data-testid="claim-last-name"
                      className="w-full px-3.5 py-2.5 rounded-lg bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-[#0a2540] focus:outline-none text-[15px] text-slate-900 placeholder-slate-400 transition-shadow"
                      placeholder="Appleseed"
                      required
                    />
                  </div>
                </div>

                <p className="text-[12px] text-slate-500 mt-3">Use the exact name shown on your payment account.</p>

                <button
                  type="submit"
                  disabled={uiState === 'submitting_name' || !firstName.trim() || !lastName.trim()}
                  data-testid="submit-claim-btn"
                  className="mt-5 w-full py-3 rounded-lg bg-[#0a2540] hover:bg-[#13335c] disabled:opacity-60 text-white font-semibold text-[15px] transition-colors"
                >
                  {uiState === 'submitting_name' ? 'Checking sender name…' : 'Verify my payment'}
                </button>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2.5">
                  <button
                    type="button"
                    onClick={() => setUiState('note_mismatch')}
                    data-testid="back-to-mismatch-btn"
                    className="w-full py-2.5 rounded-lg bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 font-medium text-[13px] transition-colors"
                  >
                    Go back
                  </button>
                  <button
                    type="button"
                    onClick={goToMerchant}
                    data-testid="continue-merchant-name"
                    className="w-full py-2.5 rounded-lg text-slate-500 hover:text-slate-800 text-[13px] font-medium transition-colors"
                  >
                    Back to gamevault999.world
                  </button>
                </div>
              </form>
            )}

            {/* ====================== STATE: MANUAL REVIEW ====================== */}
            {uiState === 'manual_review' && (
              <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-6" data-testid="state-manual-review">
                <h2 className="text-[17px] font-semibold text-slate-900 leading-tight">We couldn’t verify automatically</h2>
                <p className="text-[13px] text-slate-500 mt-1">
                  Your payment may still be processing, or the sender name may not match. You can try another name or contact support.
                </p>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => { setFirstName(''); setLastName(''); setUiState('ask_sender_name'); }}
                    data-testid="try-another-name-btn"
                    className="w-full py-2.5 rounded-lg bg-[#0a2540] hover:bg-[#13335c] text-white font-medium text-[14px] transition-colors"
                  >
                    Try another name
                  </button>
                  <a
                    href={`mailto:support@1tappay.vip?subject=Payment%20review%20${encodeURIComponent(orderId)}`}
                    data-testid="contact-support-btn"
                    className="w-full py-2.5 rounded-lg bg-white ring-1 ring-slate-200 hover:bg-slate-50 text-slate-700 font-medium text-[14px] transition-colors text-center"
                  >
                    Contact support
                  </a>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2.5">
                  <button
                    type="button"
                    onClick={() => { setUiState('checking'); startPolling(); }}
                    data-testid="resume-polling-btn"
                    className="w-full py-2.5 rounded-lg text-slate-500 hover:text-slate-800 text-[13px] font-medium transition-colors"
                  >
                    Check again
                  </button>
                  <button
                    type="button"
                    onClick={goToMerchant}
                    data-testid="continue-merchant-review"
                    className="w-full py-2.5 rounded-lg text-slate-500 hover:text-slate-800 text-[13px] font-medium transition-colors"
                  >
                    Back to gamevault999.world
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Desktop summary */}
          <SummaryCard className="hidden lg:block sticky top-6" />
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto border-t border-slate-200 bg-white/50">
        <div className="max-w-[1040px] mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[12px] text-slate-500">
          <span>© {new Date().getFullYear()} 1TapPay · Secure hosted checkout</span>
          <span className="inline-flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m6 4H6a2 2 0 01-2-2v-6a2 2 0 012-2h12a2 2 0 012 2v6a2 2 0 01-2 2zM8 11V7a4 4 0 118 0v4" />
            </svg>
            Encrypted · Verified by payment note
          </span>
        </div>
      </footer>
    </div>
  );
};

export default PublicCheckoutPage;
